import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, Tooltip } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import dayjs from 'dayjs';
import { Navigate } from 'react-router-dom';

import PageWrapper from '../../common/PageWrapper';
import { DataTable, Column } from '../../common/DataTable';
import { Pagination } from '../../common/DataTable/Pagination';
import { Button } from '../../components/core';
import { RefreshButton } from '../Paylinks/components/RefreshButton';
import { useIsSkipifyAdmin } from '../../hooks';
import { useMerchantAwareNavigate } from '../../hooks/useMerchantAwareNavigate';
import { useListOnboardingsQuery } from '../../store/modules/merchant/api';
import type { ShopifyMerchantOnboarding } from '../../store/modules/merchant/types';
import { configurationPaths } from '../../util/constants';
import { toast } from '../../util/toast';
import {
  buttonPaddingSx,
  getCurrentStepLabel,
  getProvisioningStatus,
  isOnboardingComplete,
} from './utils';
import { AddShopifyOnboardingDialog } from './AddShopifyOnboardingDialog';
import { StatusChip } from './StatusChip';

const columns: Column<ShopifyMerchantOnboarding>[] = [
  { field: 'merchantName', headerName: 'Merchant name', minWidth: 180 },
  { field: 'externalPlatformId', headerName: 'Shop domain', minWidth: 190 },
  {
    id: 'currentStep',
    headerName: 'Current step',
    // Nothing is outstanding once every step is done. The completion badge lives in Status, the one
    // column carrying colour, rather than being said twice in the same row.
    valueGetter: (row) => (isOnboardingComplete(row) ? '—' : getCurrentStepLabel(row)),
    minWidth: 150,
  },
  {
    id: 'status',
    headerName: 'Status',
    // Where the row stands. The step name says what is outstanding but not whether it is anyone's
    // move here, which is what the queue is actually scanned for: colour means an admin has to act
    // today, grey means the onboarding is parked with the merchant.
    renderCell: (row) => {
      const status = getProvisioningStatus(row);
      const label = isOnboardingComplete(row) ? (
        <>
          <CheckCircle sx={{ fontSize: 14 }} />
          {status.label}
        </>
      ) : (
        status.label
      );

      return (
        <Tooltip title={status.reason ?? ''}>
          <span>
            <StatusChip label={label} color={status.color} />
          </span>
        </Tooltip>
      );
    },
    minWidth: 160,
  },
  {
    field: 'updatedAt',
    headerName: 'Last updated',
    valueGetter: (row) => (row.updatedAt ? dayjs(row.updatedAt).format('MMM D, YYYY h:mm A') : '—'),
    minWidth: 180,
  },
];

const ShopifyProvisioning = () => {
  const isSkipifyAdmin = useIsSkipifyAdmin();
  const navigate = useMerchantAwareNavigate();
  const tableRef = useRef<HTMLDivElement>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const {
    data: response,
    isFetching,
    isError,
    refetch,
  } = useListOnboardingsQuery({
    pageNumber,
    pageSize,
    systemOfOrigin: 'shopify',
  });

  useEffect(() => {
    if (isError) toast.error('Unable to load Shopify provisioning queue.');
  }, [isError]);

  if (!isSkipifyAdmin) {
    return <Navigate to={configurationPaths.SETTINGS} replace />;
  }

  return (
    <PageWrapper
      data-testid="SHOPIFY_PROVISIONING"
      title="Shopify provisioning"
      description="Complete Shopify Partner setup."
      isLoading={isFetching && !response}
    >
      <Stack gap={2}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <RefreshButton onClick={() => refetch()} disabled={isFetching} />
          <Button
            text="Add Shopify onboarding"
            sx={buttonPaddingSx}
            onClick={() => setIsAddDialogOpen(true)}
          />
        </Box>
        <DataTable<ShopifyMerchantOnboarding>
          rows={(response?.data ?? []).filter(
            (row): row is ShopifyMerchantOnboarding => row.systemOfOrigin === 'shopify',
          )}
          columns={[
            ...columns,
            {
              id: 'actions',
              headerName: 'Actions',
              align: 'right',
              renderCell: (row) => (
                <Button
                  text="Open"
                  variant="text"
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={() =>
                    navigate(`${configurationPaths.ADMIN_SHOPIFY_PROVISIONING}/${row.merchantId}`)
                  }
                />
              ),
              minWidth: 100,
            },
          ]}
          getRowId={(row) => row.merchantId}
          isLoading={isFetching}
          pageSize={pageSize}
          tableHeight="calc(100vh - 360px)"
          tableRef={tableRef}
          noRowsMessage="No Shopify onboarding entries. Add a merchant to start provisioning."
          renderPagination={() => (
            <Pagination
              itemCount={response?.data.length ?? 0}
              itemTotal={response?.metadata.totalRecords ?? 0}
              pageCount={response?.metadata.totalPages ?? 0}
              page={pageNumber}
              pageSize={pageSize}
              disabled={isFetching}
              onPageChange={(_, page) => {
                setPageNumber(page);
                tableRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              pageSizeOptions={[10, 25, 50]}
              onPageSizeChange={(newPageSize) => {
                setPageNumber(1);
                setPageSize(newPageSize);
              }}
            />
          )}
        />
      </Stack>
      <AddShopifyOnboardingDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
      />
    </PageWrapper>
  );
};

export default ShopifyProvisioning;
